// src/components/shift/ShiftEndDialog.jsx - 移除明细表合计行
import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, TrendingUp, ShoppingBag, DollarSign, CreditCard, Package } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { translations } from '../../utils/translations';
import { saleApi } from '../../api';

const ShiftEndDialog = ({ shift, onClose, onSubmit }) => {
  const { language } = useApp();
  const t = translations[language];
  
  const [closingCash, setClosingCash] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [shiftStats, setShiftStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);

  // 加载班次统计
  useEffect(() => {
    loadShiftStats();
  }, []);

  // 加载班次统计数据
  const loadShiftStats = async () => {
    setLoadingStats(true);
    try {
      console.log('📊 正在加载班次统计...', { shift_id: shift.id });
      
      // 获取当前班次的所有销售记录
      const res = await saleApi.getList({ shift_id: shift.id });
      
      console.log('✅ 销售记录响应:', res);
      
      if (res.success) {
        const sales = res.data || [];
        
        console.log(`📦 班次 #${shift.id} 共有 ${sales.length} 条销售记录`);
        
        // 计算统计数据
        const stats = {
          // 基本统计
          totalOrders: sales.length,
          totalSales: sales.reduce((sum, sale) => sum + parseFloat(sale.total_amount || 0), 0),
          totalItems: sales.reduce((sum, sale) => sum + parseInt(sale.total_items || 0), 0),
          
          // 支付方式统计
          cashSales: sales
            .filter(s => s.payment_method === 'cash')
            .reduce((sum, sale) => sum + parseFloat(sale.total_amount || 0), 0),
          cashOrders: sales.filter(s => s.payment_method === 'cash').length,
          
          cardSales: sales
            .filter(s => s.payment_method === 'card')
            .reduce((sum, sale) => sum + parseFloat(sale.total_amount || 0), 0),
          cardOrders: sales.filter(s => s.payment_method === 'card').length,
          
          mobileSales: sales
            .filter(s => s.payment_method === 'mobile')
            .reduce((sum, sale) => sum + parseFloat(sale.total_amount || 0), 0),
          mobileOrders: sales.filter(s => s.payment_method === 'mobile').length,
          
          // 商品统计
          productStats: calculateProductStats(sales),
        };
        
        setShiftStats(stats);
        
        // 自动填充预期现金（期初现金 + 现金销售）
        const expectedCash = parseFloat(shift.opening_cash || 0) + stats.cashSales;
        setClosingCash(expectedCash.toFixed(2));
        
        console.log('✅ 班次统计完成:', stats);
      }
    } catch (err) {
      console.error('❌ 加载班次统计失败:', err);
    } finally {
      setLoadingStats(false);
    }
  };

  // 计算商品销售统计
  const calculateProductStats = (sales) => {
    const productMap = {};
    
    sales.forEach(sale => {
      if (sale.items && Array.isArray(sale.items)) {
        sale.items.forEach(item => {
          const key = item.product_id;
          if (!productMap[key]) {
            productMap[key] = {
              product_id: item.product_id,
              product_name: item.product_name,
              product_name_en: item.product_name_en,
              product_name_km: item.product_name_km,
              quantity: 0,
              amount: 0,
            };
          }
          productMap[key].quantity += parseInt(item.quantity || 0);
          productMap[key].amount += parseFloat(item.subtotal || (item.unit_price * item.quantity) || 0);
        });
      }
    });
    
    // 转换为数组并按销量排序
    return Object.values(productMap)
      .sort((a, b) => b.quantity - a.quantity);
  };

  // 获取商品显示名称
  const getProductName = (product) => {
    switch (language) {
      case 'en':
        return product.product_name_en || product.product_name;
      case 'km':
        return product.product_name_km || product.product_name;
      default:
        return product.product_name;
    }
  };

  // 计算预期现金（使用统计数据）
  const expectedCash = shiftStats 
    ? parseFloat(shift.opening_cash || 0) + shiftStats.cashSales
    : parseFloat(shift.opening_cash || 0) + parseFloat(shift.total_sales || 0);
  
  const difference = closingCash ? parseFloat(closingCash) - expectedCash : 0;

  // 提交
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!closingCash) {
      alert(
        language === 'en' ? 'Please enter closing cash amount' :
        language === 'km' ? 'សូមបញ្ចូលចំនួនទឹកប្រាក់បិទ' :
        '请输入期末现金金额'
      );
      return;
    }

    setLoading(true);

    try {
      // 构建提交数据
      const data = {
        shift_id: parseInt(shift.id),
        closing_cash: parseFloat(parseFloat(closingCash).toFixed(2)),
        notes: notes.trim(),
        
        // 添加统计数据
        total_sales: shiftStats ? parseFloat(shiftStats.totalSales.toFixed(2)) : 0,
        total_orders: shiftStats ? shiftStats.totalOrders : 0,
        cash_difference: parseFloat(difference.toFixed(2)),
        end_time: new Date().toISOString(),
        
        // 支付方式统计
        cash_sales: shiftStats ? parseFloat(shiftStats.cashSales.toFixed(2)) : 0,
        card_sales: shiftStats ? parseFloat(shiftStats.cardSales.toFixed(2)) : 0,
        mobile_sales: shiftStats ? parseFloat(shiftStats.mobileSales.toFixed(2)) : 0,
      };

      console.log('📤 提交结束班次数据:', JSON.stringify(data, null, 2));

      await onSubmit(data);
    } catch (err) {
      console.error('❌ 提交失败:', err);
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* 头部 */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h3 className="text-xl font-bold">
              {language === 'en' ? 'End Shift' :
               language === 'km' ? 'បញ្ចប់វេន' :
               '结束班次'}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {language === 'en' ? 'Review statistics and close shift' :
               language === 'km' ? 'ពិនិត្យស្ថិតិនិងបិទវេន' :
               '审核统计并关闭班次'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* 内容区 */}
        <div className="flex-1 overflow-y-auto p-6">
          {loadingStats ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">
                {language === 'en' ? 'Loading statistics...' :
                 language === 'km' ? 'កំពុងផ្ទុកស្ថិតិ...' :
                 '加载统计数据...'}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* 销售统计卡片 */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <TrendingUp className="w-5 h-5 mr-2 text-green-600" />
                  {language === 'en' ? 'Sales Statistics' :
                   language === 'km' ? 'ស្ថិតិការលក់' :
                   '销售统计'}
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  {/* 订单总数 */}
                  <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-100 text-sm">
                          {language === 'en' ? 'Total Orders' :
                           language === 'km' ? 'ការបញ្ជាទិញសរុប' :
                           '订单总数'}
                        </p>
                        <p className="text-2xl font-bold mt-1">
                          {shiftStats?.totalOrders || 0}
                        </p>
                      </div>
                      <ShoppingBag className="w-8 h-8 text-green-200" />
                    </div>
                  </div>

                  {/* 销售总额 */}
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100 text-sm">
                          {language === 'en' ? 'Total Sales' :
                           language === 'km' ? 'ការលក់សរុប' :
                           '销售总额'}
                        </p>
                        <p className="text-2xl font-bold mt-1">
                          ¥{(shiftStats?.totalSales || 0).toFixed(2)}
                        </p>
                      </div>
                      <DollarSign className="w-8 h-8 text-blue-200" />
                    </div>
                  </div>

                  {/* 售出商品总数 */}
                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-100 text-sm">
                          {language === 'en' ? 'Items Sold' :
                           language === 'km' ? 'ទំនិញបានលក់' :
                           '售出商品'}
                        </p>
                        <p className="text-2xl font-bold mt-1">
                          {shiftStats?.totalItems || 0}
                          <span className="text-sm font-normal text-purple-100 ml-1">
                            {language === 'en' ? 'pcs' :
                             language === 'km' ? 'ក្បាល' :
                             '件'}
                          </span>
                        </p>
                      </div>
                      <Package className="w-8 h-8 text-purple-200" />
                    </div>
                  </div>
                </div>
              </div>

              {/* 支付方式统计 */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <CreditCard className="w-5 h-5 mr-2 text-blue-600" />
                  {language === 'en' ? 'Payment Methods' :
                   language === 'km' ? 'វិធីបង់ប្រាក់' :
                   '支付方式统计'}
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  {/* 现金 */}
                  <div className="border rounded-lg p-4 bg-green-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        {language === 'en' ? 'Cash' : language === 'km' ? 'សាច់ប្រាក់' : '现金'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {shiftStats?.cashOrders || 0} {language === 'en' ? 'orders' : language === 'km' ? 'បញ្ជាទិញ' : '笔'}
                      </span>
                    </div>
                    <p className="text-xl font-bold text-green-600">
                      ¥{(shiftStats?.cashSales || 0).toFixed(2)}
                    </p>
                  </div>

                  {/* 刷卡 */}
                  <div className="border rounded-lg p-4 bg-blue-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        {language === 'en' ? 'Card' : language === 'km' ? 'កាត' : '刷卡'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {shiftStats?.cardOrders || 0} {language === 'en' ? 'orders' : language === 'km' ? 'បញ្ជាទិញ' : '笔'}
                      </span>
                    </div>
                    <p className="text-xl font-bold text-blue-600">
                      ¥{(shiftStats?.cardSales || 0).toFixed(2)}
                    </p>
                  </div>

                  {/* 移动支付 */}
                  <div className="border rounded-lg p-4 bg-purple-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        {language === 'en' ? 'Mobile' : language === 'km' ? 'ទូរសព្ទ' : '移动支付'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {shiftStats?.mobileOrders || 0} {language === 'en' ? 'orders' : language === 'km' ? 'បញ្ជាទិញ' : '笔'}
                      </span>
                    </div>
                    <p className="text-xl font-bold text-purple-600">
                      ¥{(shiftStats?.mobileSales || 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              {/* 销售商品明细 - 移除了合计行 */}
              {shiftStats?.productStats && shiftStats.productStats.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <Package className="w-5 h-5 mr-2 text-orange-600" />
                    {language === 'en' ? 'Products Sold' :
                     language === 'km' ? 'ផលិតផលបានលក់' :
                     '销售商品明细'}
                    <span className="ml-2 text-sm text-gray-500 font-normal">
                      ({shiftStats.productStats.length} {language === 'en' ? 'types' : language === 'km' ? 'ប្រភេទ' : '种'})
                    </span>
                  </h4>
                  <div className="border rounded-lg overflow-hidden max-h-80 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            {language === 'en' ? 'No.' : language === 'km' ? 'លេខ' : '序号'}
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            {language === 'en' ? 'Product' : language === 'km' ? 'ផលិតផល' : '商品'}
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                            {language === 'en' ? 'Qty' : language === 'km' ? 'ចំនួន' : '数量'}
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                            {language === 'en' ? 'Amount' : language === 'km' ? 'ចំនួនទឹកប្រាក់' : '金额'}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {shiftStats.productStats.map((product, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-2 text-gray-500">
                              {index + 1}
                            </td>
                            <td className="px-4 py-2">
                              <div className="font-medium text-gray-900">
                                {getProductName(product)}
                              </div>
                            </td>
                            <td className="px-4 py-2 text-right font-medium text-gray-700">
                              {product.quantity}
                            </td>
                            <td className="px-4 py-2 text-right font-medium text-blue-600">
                              ¥{product.amount.toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* 现金核对 */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <h4 className="font-semibold text-gray-800 mb-3">
                  {language === 'en' ? 'Cash Reconciliation' :
                   language === 'km' ? 'ការផ្គូផ្គងសាច់ប្រាក់' :
                   '现金核对'}
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {language === 'en' ? 'Opening Cash:' :
                       language === 'km' ? 'ទឹកប្រាក់បើក:' :
                       '期初现金:'}
                    </span>
                    <span className="font-medium">¥{parseFloat(shift.opening_cash || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {language === 'en' ? 'Cash Sales:' :
                       language === 'km' ? 'ការលក់សាច់ប្រាក់:' :
                       '现金收入:'}
                    </span>
                    <span className="font-medium text-green-600">
                      +¥{(shiftStats?.cashSales || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm pt-2 border-t">
                    <span className="font-medium text-gray-700">
                      {language === 'en' ? 'Expected Cash:' :
                       language === 'km' ? 'សាច់ប្រាក់រំពឹងទុក:' :
                       '预期现金:'}
                    </span>
                    <span className="font-bold">¥{expectedCash.toFixed(2)}</span>
                  </div>

                  {/* 实际现金输入 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {language === 'en' ? 'Actual Closing Cash *' :
                       language === 'km' ? 'ទឹកប្រាក់បិទជាក់ស្តែង *' :
                       '实际期末现金 *'}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={closingCash}
                      onChange={(e) => setClosingCash(e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                      required
                      disabled={loading}
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      {language === 'en' ? 'Count all cash in the register' :
                       language === 'km' ? 'រាប់ទឹកប្រាក់ទាំងអស់ក្នុងម៉ាស៊ីនគិតលុយ' :
                       '清点收银台中的所有现金'}
                    </p>
                  </div>

                  {/* 差额显示 */}
                  {closingCash && (
                    <div className={`rounded-lg p-3 ${
                      Math.abs(difference) < 0.01 ? 'bg-green-50 border border-green-200' :
                      difference > 0 ? 'bg-blue-50 border border-blue-200' :
                      'bg-red-50 border border-red-200'
                    }`}>
                      <div className="flex items-start space-x-2">
                        {Math.abs(difference) >= 0.01 && (
                          <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                            difference > 0 ? 'text-blue-600' : 'text-red-600'
                          }`} />
                        )}
                        <div className="flex-1">
                          <p className={`font-semibold ${
                            Math.abs(difference) < 0.01 ? 'text-green-700' :
                            difference > 0 ? 'text-blue-700' :
                            'text-red-700'
                          }`}>
                            {Math.abs(difference) < 0.01 ? (
                              language === 'en' ? '✓ Cash balanced' :
                              language === 'km' ? '✓ ទឹកប្រាក់ស្មើ' :
                              '✓ 现金平衡'
                            ) : difference > 0 ? (
                              language === 'en' ? `Overage: ¥${difference.toFixed(2)}` :
                              language === 'km' ? `លើស: ¥${difference.toFixed(2)}` :
                              `多出: ¥${difference.toFixed(2)}`
                            ) : (
                              language === 'en' ? `Shortage: ¥${Math.abs(difference).toFixed(2)}` :
                              language === 'km' ? `ខ្វះ: ¥${Math.abs(difference).toFixed(2)}` :
                              `短缺: ¥${Math.abs(difference).toFixed(2)}`
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* 备注 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'en' ? 'Notes (Optional)' :
                   language === 'km' ? 'កំណត់ចំណាំ (ស្រេចចិត្ត)' :
                   '备注 (可选)'}
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  placeholder={
                    language === 'en' ? 'Add any notes about this shift...' :
                    language === 'km' ? 'បន្ថែមកំណត់ចំណាំអំពីវេននេះ...' :
                    '添加关于本班次的任何备注...'
                  }
                  disabled={loading}
                />
              </div>

              {/* 按钮 */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={loading}
                >
                  {t.cancel}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  disabled={loading}
                >
                  {loading ? (
                    language === 'en' ? 'Ending...' :
                    language === 'km' ? 'កំពុងបញ្ចប់...' :
                    '结束中...'
                  ) : (
                    language === 'en' ? 'End Shift' :
                    language === 'km' ? 'បញ្ចប់វេន' :
                    '结束班次'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShiftEndDialog;